import {
  useBackOfficeUsersApi,
  useBackOfficeWorkspacesApi,
  useCreateUserApi,
  useCreateWorkspaceApi,
  useDeleteWorkspaceApi,
  useBulkDeleteWorkspacesApi,
  useBulkDeleteUsersApi,
  useUpdateUserTypeApi,
  useUpdateUserWorkspacesApi,
  useUpdateWorkspaceMembersApi,
  useAdminUpdateWorkspaceApi,
  useAdminReferencesApi,
  useAdminCreateReferenceApi,
  useAdminUpdateReferenceApi,
  useAdminDeleteReferenceApi,
  useAdminUpdatesApi,
  useAdminCreateUpdateApi,
  useAdminUpdateUpdateApi,
  useAdminDeleteUpdateApi,
  useAdminDiscardUpdateApi,
  useAdminVersionsApi,
  useAdminCreateVersionApi,
  useAdminUpdateVersionApi,
  useAdminDeleteVersionApi,
  useAdminUploadVersionFileApi,
  useAdminDeleteVersionFileApi,
  useAdminUploadUpdateFileApi,
  useAdminDeleteUpdateFileApi,
  useAdminSourcesApi,
  useAdminCreateSourceApi,
  useAdminUpdateSourceApi,
  useAdminDeleteSourceApi,
  useAdminUploadSourceIconApi,
  useAdminInstitutionsApi,
  useAdminCreateInstitutionApi,
  useAdminUpdateInstitutionApi,
  useAdminDeleteInstitutionApi,
  useAdminUpdateDetailApi,
  useAdminGenerateRedlineApi,
  useAdminGenerateSummaryApi,
  useAdminReferenceDetailApi,
  useAdminUpdateReferenceDetailApi,
  useStartSourceScrapingApi,
  useGetSourceScrapingStatusApi,
  useConfirmSourceScrapingApi,
  useCancelSourceScrapingApi,
  useDiscardedItemsApi,
  useAdminCountriesApi,
  useAdminCreateCountryApi,
  useAdminUpdateCountryApi,
  useAdminDeleteCountryApi,
  useAdminConsultantOrgsApi,
  useAdminCreateConsultantOrgApi,
  useAdminUpdateConsultantOrgApi,
  useAdminDeleteConsultantOrgApi,
  useAdminUploadConsultantOrgLogoApi,
  useAdminApiClientsApi,
  useCreateApiClientApi,
  useUpdateApiClientApi,
  useDeleteApiClientApi,
} from '@/api/backoffice';
import { useAuthenticatedClient } from '@/api/useApi';
import type {
  AdminListReferencesQuery,
  AdminListUpdatesQuery,
} from '@ditto/shared';
import { useMemo } from 'react';

export const useBackOfficeUsersService = () => {
  const { data: users, isLoading, error } = useBackOfficeUsersApi();
  const { isLoaded, isSignedIn } = useAuthenticatedClient();

  const transformedUsers = useMemo(() => users || [], [users]);

  return {
    users: transformedUsers,
    isLoading,
    error,
    isLoaded,
    isSignedIn,
  };
};

export const useBackOfficeWorkspacesService = () => {
  const { data: workspaces, isLoading, error } = useBackOfficeWorkspacesApi();
  const { isLoaded, isSignedIn } = useAuthenticatedClient();

  const transformedWorkspaces = useMemo(() => workspaces || [], [workspaces]);

  return {
    workspaces: transformedWorkspaces,
    isLoading,
    error,
    isLoaded,
    isSignedIn,
  };
};

export const useUpdateUserTypeService = () => {
  const updateUserTypeMutation = useUpdateUserTypeApi();

  return {
    updateUserType: updateUserTypeMutation.mutate,
    isLoading: updateUserTypeMutation.isPending,
    error: updateUserTypeMutation.error,
  };
};

export const useCreateUserService = () => {
  const createUserMutation = useCreateUserApi();

  return {
    createUser: createUserMutation.mutate,
    isLoading: createUserMutation.isPending,
    error: createUserMutation.error,
  };
};

export const useCreateWorkspaceService = () => {
  const createWorkspaceMutation = useCreateWorkspaceApi();

  return {
    createWorkspace: createWorkspaceMutation.mutate,
    isLoading: createWorkspaceMutation.isPending,
    error: createWorkspaceMutation.error,
  };
};

export const useUpdateUserWorkspacesService = () => {
  const updateUserWorkspacesMutation = useUpdateUserWorkspacesApi();

  return {
    updateUserWorkspaces: updateUserWorkspacesMutation.mutate,
    isPending: updateUserWorkspacesMutation.isPending,
    error: updateUserWorkspacesMutation.error,
  };
};

export const useDeleteWorkspaceService = () => {
  const deleteWorkspaceMutation = useDeleteWorkspaceApi();

  return {
    deleteWorkspace: deleteWorkspaceMutation.mutate,
    isPending: deleteWorkspaceMutation.isPending,
    error: deleteWorkspaceMutation.error,
  };
};

export const useBulkDeleteWorkspacesService = () => {
  const bulkDeleteMutation = useBulkDeleteWorkspacesApi();

  return {
    bulkDeleteWorkspaces: bulkDeleteMutation.mutate,
    isPending: bulkDeleteMutation.isPending,
    error: bulkDeleteMutation.error,
  };
};

export const useBulkDeleteUsersService = () => {
  const bulkDeleteMutation = useBulkDeleteUsersApi();

  return {
    bulkDeleteUsers: bulkDeleteMutation.mutate,
    isPending: bulkDeleteMutation.isPending,
    error: bulkDeleteMutation.error,
  };
};

export const useUpdateWorkspaceMembersService = () => {
  const updateWorkspaceMembersMutation = useUpdateWorkspaceMembersApi();

  return {
    updateWorkspaceMembers: updateWorkspaceMembersMutation.mutate,
    isLoading: updateWorkspaceMembersMutation.isPending,
    error: updateWorkspaceMembersMutation.error,
  };
};

// ── Admin Update Workspace (unified) ──

export const useAdminUpdateWorkspaceService = () => {
  const mutation = useAdminUpdateWorkspaceApi();

  return {
    updateWorkspace: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

// ── Admin Regulatory Watch ──

export const useAdminReferencesService = (
  filters?: AdminListReferencesQuery
) => {
  const { data, isPending, isFetching } = useAdminReferencesApi(filters);
  return {
    references: data ?? [],
    isLoading: isPending,
    isFetching,
  };
};

export const useAdminCreateReferenceService = () => {
  const mutation = useAdminCreateReferenceApi();
  return {
    createReference: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminDeleteReferenceService = () => {
  const mutation = useAdminDeleteReferenceApi();
  return {
    deleteReference: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminUpdateReferenceService = () => {
  const mutation = useAdminUpdateReferenceApi();
  return {
    updateReference: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminUpdatesService = (filters?: AdminListUpdatesQuery) => {
  const { data, isPending, isFetching } = useAdminUpdatesApi(filters);
  return {
    updates: data ?? [],
    isLoading: isPending,
    isFetching,
  };
};

export const useAdminCreateUpdateService = () => {
  const mutation = useAdminCreateUpdateApi();
  return {
    createUpdate: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminUpdateUpdateService = () => {
  const mutation = useAdminUpdateUpdateApi();
  return {
    updateUpdate: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminDeleteUpdateService = () => {
  const mutation = useAdminDeleteUpdateApi();
  return {
    deleteUpdate: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminDiscardUpdateService = () => {
  const mutation = useAdminDiscardUpdateApi();
  return {
    discardUpdate: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminVersionsService = (referenceId?: string) => {
  const { data, isPending } = useAdminVersionsApi(referenceId);
  return {
    versions: data ?? [],
    isLoading: isPending,
  };
};

export const useAdminCreateVersionService = () => {
  const mutation = useAdminCreateVersionApi();
  return {
    createVersion: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminUpdateVersionService = () => {
  const mutation = useAdminUpdateVersionApi();
  return {
    updateVersion: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminDeleteVersionService = () => {
  const mutation = useAdminDeleteVersionApi();
  return {
    deleteVersion: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminUploadVersionFileService = () => {
  const mutation = useAdminUploadVersionFileApi();
  return {
    uploadFile: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminDeleteVersionFileService = () => {
  const mutation = useAdminDeleteVersionFileApi();
  return {
    deleteFile: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminUploadUpdateFileService = () => {
  const mutation = useAdminUploadUpdateFileApi();
  return {
    uploadFile: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminDeleteUpdateFileService = () => {
  const mutation = useAdminDeleteUpdateFileApi();
  return {
    deleteFile: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

// ── Admin Reference Detail ──

export const useAdminReferenceDetailService = (
  referenceId: string | undefined
) => {
  const { data, isPending, isFetching } =
    useAdminReferenceDetailApi(referenceId);
  return {
    detail: data ?? null,
    isLoading: isPending,
    isFetching,
  };
};

export const useAdminUpdateReferenceDetailService = () => {
  const mutation = useAdminUpdateReferenceDetailApi();
  return {
    updateReferenceDetail: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

// ── Admin Update Detail + Redline ──

export const useAdminUpdateDetailService = (updateId: string | undefined) => {
  const { data, isPending, isFetching } = useAdminUpdateDetailApi(updateId);
  return {
    detail: data ?? null,
    isLoading: isPending,
    isFetching,
  };
};

export const useAdminGenerateRedlineService = () => {
  const mutation = useAdminGenerateRedlineApi();
  return {
    generateRedline: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminGenerateSummaryService = () => {
  const mutation = useAdminGenerateSummaryApi();
  return {
    generateSummary: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

// ── Admin Sources ──

export const useAdminSourcesService = () => {
  const { data, isPending } = useAdminSourcesApi();
  const sources = useMemo(
    () => (data ?? []).filter(s => s.id !== 'unknown'),
    [data]
  );
  return {
    sources,
    isLoading: isPending,
  };
};

export const useAdminCreateSourceService = () => {
  const mutation = useAdminCreateSourceApi();
  return {
    createSource: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminUpdateSourceService = () => {
  const mutation = useAdminUpdateSourceApi();
  return {
    updateSource: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminDeleteSourceService = () => {
  const mutation = useAdminDeleteSourceApi();
  return {
    deleteSource: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminUploadSourceIconService = () => {
  const mutation = useAdminUploadSourceIconApi();
  return {
    uploadIcon: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

// ── Admin Institutions ──

export const useAdminInstitutionsService = () => {
  const { data, isPending } = useAdminInstitutionsApi();
  return {
    institutions: data ?? [],
    isLoading: isPending,
  };
};

export const useAdminCreateInstitutionService = () => {
  const mutation = useAdminCreateInstitutionApi();
  return {
    createInstitution: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminUpdateInstitutionService = () => {
  const mutation = useAdminUpdateInstitutionApi();
  return {
    updateInstitution: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminDeleteInstitutionService = () => {
  const mutation = useAdminDeleteInstitutionApi();
  return {
    deleteInstitution: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

// ── Source Scraping ──

export const useStartSourceScrapingService = () => {
  const mutation = useStartSourceScrapingApi();
  return {
    startScraping: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useSourceScrapingStatusService = (
  workflowId: string | null,
  enabled: boolean
) => {
  const { data, isPending, error } = useGetSourceScrapingStatusApi(
    workflowId,
    enabled
  );
  return { status: data ?? null, isLoading: isPending, error };
};

export const useConfirmSourceScrapingService = () => {
  const mutation = useConfirmSourceScrapingApi();
  return {
    confirm: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useCancelSourceScrapingService = () => {
  const mutation = useCancelSourceScrapingApi();
  return {
    cancel: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

// ── Discarded Source Items ──

export const useDiscardedItemsService = (source?: string) => {
  const { data, isPending, isFetching } = useDiscardedItemsApi(source);
  return {
    items: data ?? [],
    isLoading: isPending,
    isFetching,
  };
};

// ── Countries ──

export const useAdminCountriesService = () => {
  const { data, isPending } = useAdminCountriesApi();
  return {
    countries: data ?? [],
    isLoading: isPending,
  };
};

export const useAdminCreateCountryService = () => {
  const mutation = useAdminCreateCountryApi();
  return {
    createCountry: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminUpdateCountryService = () => {
  const mutation = useAdminUpdateCountryApi();
  return {
    updateCountry: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminDeleteCountryService = () => {
  const mutation = useAdminDeleteCountryApi();
  return {
    deleteCountry: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

// ── Consultant Organizations ──

export const useAdminConsultantOrgsService = () => {
  const { data, isPending } = useAdminConsultantOrgsApi();
  return {
    consultantOrgs: data ?? [],
    isLoading: isPending,
  };
};

export const useAdminCreateConsultantOrgService = () => {
  const mutation = useAdminCreateConsultantOrgApi();
  return {
    createOrg: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminUpdateConsultantOrgService = () => {
  const mutation = useAdminUpdateConsultantOrgApi();
  return {
    updateOrg: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminDeleteConsultantOrgService = () => {
  const mutation = useAdminDeleteConsultantOrgApi();
  return {
    deleteOrg: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};

export const useAdminUploadConsultantOrgLogoService = () => {
  const mutation = useAdminUploadConsultantOrgLogoApi();
  return {
    uploadLogo: mutation.mutate,
    isPending: mutation.isPending,
  };
};

// ── API Clients ──

export const useAdminApiClientsService = () => {
  const { data, isPending } = useAdminApiClientsApi();
  return {
    apiClients: data ?? [],
    isLoading: isPending,
  };
};

export const useAdminCreateApiClientService = () => {
  const mutation = useCreateApiClientApi();
  return {
    createApiClient: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
};

export const useAdminUpdateApiClientService = () => {
  const mutation = useUpdateApiClientApi();
  return {
    updateApiClient: mutation.mutate,
    isPending: mutation.isPending,
  };
};

export const useAdminDeleteApiClientService = () => {
  const mutation = useDeleteApiClientApi();
  return {
    deleteApiClient: mutation.mutate,
    isPending: mutation.isPending,
  };
};
